import { QueryHookOptions, gql } from '@apollo/client'
import { formatReactions } from 'app/features/group/helpers'
import { CarouselGroupAdBlockFragment, GroupAdBlock, LargeGroupAdBlockFragment, StandardGroupAdBlockFragment } from 'app/features/ads/blocks'
import { GroupAnnouncement, GroupEvent, GroupPost, ReactionType } from 'app/types'
import { usePersistedCacheQuery } from 'app/hooks'

export const query = gql`
  query fetchGroupPosts($id: String!, $searchTerm: String, $page: Int!, $limit: Int!, $sort: String) {
    searchGroupPosts(id: $id, searchTerm: $searchTerm, page: $page, limit: $limit, sort: $sort) {
      totalDocs
      page
      ads {
        id
        preferredPosition
        isBrandAd
        brand {
          id
          name
          logo {
            url
          alt
          }
          slug
        }
        title
        logo {
          url
          alt
        }
        ad {
          ${CarouselGroupAdBlockFragment.partialFragment}
          ${LargeGroupAdBlockFragment.partialFragment}
          ${StandardGroupAdBlockFragment.partialFragment}
        }
      }
      docs {
        id
        type
        createdAt
        editedAt
        text
        link
        totalComments
        creator {
          id
          fullName
          avatar {
            id
            sizes {
              small {
                url
              }
              medium {
                url
              }
            }
          }
          subjectOfStudy {
            id
            name
          }
          yearOfStudy
          university {
            id
            name
          }
        }
        group {
          id
          name
          profileImage {
            id
            sizes {
              medium {
                url
              }
              small {
                url
              }
            }
          }
          university {
            slug
          }
          universityCourse {
            slug
          }
          allowedUserTypes
          isOwner
          isModerator
        }
        images {
          id
          image {
            id
            width
            height
            url
          }
        }
        reactions {
          id
          reaction
          user {
            id
          }
        }

        # Announcement
        body
        scheduledAt

        # Event
        event {
          id
          name
          description
          startDate
          endDate
          createdAt
          location
          image {
            id
            url
            width
            height
          }
        }
      }
    }
  }
`

export enum SearchGroupPostsSort {
  DateAsc = 'date',
  DateDesc = '-date',
  LikesAsc = 'reactions',
  LikesDesc = '-reactions',
}


export interface GroupPostsQueryInput {
  /**
   * This is the Group ID
   */
  id: string
  searchTerm?: string
  limit?: number
  sort?: SearchGroupPostsSort
  page?: number
}

export interface GroupPostsQueryOutput {
  searchGroupPosts: {
    totalDocs: number
    page: number
    ads: {
      id: string
      isBrandAd: boolean
      brand?: {
        id: string
        name: string
        logo: {
          url: string
          alt: string
        }
        slug: string
      }
      title?: string
      logo?: {
        url: string
        alt: string
      }
      link?: string
      ad: GroupAdBlock[]
    }[]
    docs: {
      type: 'post' | 'event' | 'announcement'
      id: string
      text: string
      link: string
      totalComments: number
      createdAt: string
      editedAt: string | null
      creator: {
        id: string
        fullName: string
        yearOfStudy: string
        subjectOfStudy: {
          id: string
          name: string
        }
        avatar: {
          id: string
          sizes: {
            small: {
              url: string
            }
            medium: {
              url: string
            }
          }
        }
        university: {
          id: string
          name: string
        }
      }
      reactions: {
        reaction: ReactionType
        id: string
        user: {
          id: string
        }
      }[]
      group: {
        id: string
        name: string
        profileImage: {
          sizes: {
            small: {
              url: string
            }
            medium: {
              url: string
            }
          }
        }
        university?: {
          slug: string
        }
        universityCourse?: {
          slug: string
        }
        allowedUserTypes: string[]
        isOwner: boolean
        isModerator: boolean
      }
      images: {
        id: string
        image: {
          id: string
          width: number
          height: number
          url: string
        }
      }[]
      body?: string
      scheduledAt?: string
      event?: {
        id: string
        name: string
        description: string
        location?: string
        startDate: string
        endDate?: string
        createdAt: string
        image?: {
          id: string
          url: string
          width: number
          height: number
        }
      }
    }[]
  }
}

const defaultVariables = {
  sort: SearchGroupPostsSort.DateDesc,
  searchTerm: '',
  page: 1,
  limit: 10,
}

export const useGroupPostsQuery = (
  input: GroupPostsQueryInput,
  options: QueryHookOptions<GroupPostsQueryOutput, GroupPostsQueryInput>
) =>
  usePersistedCacheQuery<GroupPostsQueryOutput, GroupPostsQueryInput>(query, {
    variables: input,
    ...options,
  })


export const formatFn = (data: GroupPostsQueryOutput | undefined): (GroupPost | GroupEvent | GroupAnnouncement)[] => {
  if (!data?.searchGroupPosts) return []
  const docs = data.searchGroupPosts?.docs || []
  return docs.map((post) => {
    const group = {
      id: post.group.id,
      name: post.group.name,
      href: `/groups/${post.group.id}`,
      profileImage: {
        small: post.group.profileImage?.sizes.small.url || '',
      },
      university: post.group.university,
      universityCourse: post.group.universityCourse,
      allowedUserTypes: post.group.allowedUserTypes || [],
      isOwner: post.group.isOwner,
      isModerator: post.group.isModerator
    }

    const creator = {
      id: post.creator.id,
      fullName: post.creator.fullName,
      avatar: {
        medium: post.creator.avatar?.sizes?.medium.url,
        small: post.creator.avatar?.sizes?.small.url,
      },
      university: post.creator.university,
      subjectOfStudy: post.creator.subjectOfStudy,
      yearOfStudy: post.creator.yearOfStudy,
    }

    const reactions = formatReactions(post.reactions)

    if (post.type === 'event' && post.event) {
      const event = post.event
      return {
        type: 'groupEvent',
        href: `/groups/events/${event.id}`,
        id: post.id,
        eventId: event.id,
        name: event.name,
        description: event.description,
        location: event.location,
        startDate: event.startDate,
        endDate: event.endDate,
        createdAt: event.createdAt,
        creator: creator,
        group: group,
        ...event.image && {
          image: {
            url: event.image.url,
            width: event.image.width,
            height: event.image.height,
          }
        }
      }
    }

    if (post.type === 'post') {
      const groupPost: GroupPost = {
        type: 'groupPost',
        id: post.id,
        text: post.text,
        href: `/groups/posts/${post.id}`,
        reactions,
        totalComments: post.totalComments || 0,
        createdAt: post.createdAt,
        editedAt: post.editedAt,
        creator: creator,
        group: group,
        image: {
          url: post.images[0]?.image?.url || '',
          width: post.images[0]?.image?.width || 1,
          height: post.images[0]?.image?.height || 1,
        },
      }
      return groupPost
    }

    if (post.type === 'announcement') {
      const reactions = formatReactions(post.reactions)
      const announcement: GroupAnnouncement = {
        type: 'groupAnnouncement',
        href: `/groups/posts/${post.id}`,
        id: post.id,
        createdAt: post.createdAt,
        editedAt: post.editedAt,
        totalComments: post.totalComments || 0,
        title: post.text,
        body: post.body,
        scheduledDate: post.scheduledAt,
        reactions,
        creator: creator,
        group: group,
      }
      return announcement
    }

  }).filter(Boolean) as (GroupPost | GroupEvent | GroupAnnouncement)[]
}

export const groupPostsQuery = {
  query,
  defaultVariables,
  formatFn,
}
